import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import PaginationItem from '@material-ui/lab/PaginationItem';
import { useDeepCompare } from '../../../hooks';
import { useDataTableContext } from '../utils/DataTableContext';
import { DATA_TABLE_PAGINATOR_HEIGHT_PX } from '../utils/DataTableHeights';
import { Pagination } from '../../Pagination/Pagination';
import { DATA_TABLE_LIMIT } from '../utils/getDataTableCount';
import { useDataTableQueryContext } from '../utils/DataTableQueryProvider/DataTableQueryProvider';

export interface PaginatorProps
  extends Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'css' | 'ref'> {
  pageIndex?: number;
  fetchNextPage?: (page: { pageIndex: number; pageSize: number }) => void;
  onPageChange?: (page: number) => void;
  // Number of always visible pages before and after the current page.
  visibleExtraPages?: number;
}

export const DataTablePaginator: FC<React.PropsWithChildren<PaginatorProps>> = ({
  pageIndex: manualPageIndex,
  fetchNextPage,
  onPageChange,
  visibleExtraPages,
  ...rest
}) => {
  const { querySettings, getState, isFetching, getPageOptions } = useDataTableContext();
  const { queryVariables, applyPage } = useDataTableQueryContext();
  const state = getState?.();
  const tablePageCount = getPageOptions?.()?.length ?? 0;
  const pageIndex = state?.pagination.pageIndex ?? 0;
  const pageSize = state?.pagination.pageSize ?? 0;
  const [pageCount, setPageCount] = useState<number>(tablePageCount);
  const pagesHasChanged = useDeepCompare({ pageIndex, pageSize });
  const limit = querySettings?.limit || DATA_TABLE_LIMIT;

  useEffect(() => {
    setPageCount(tablePageCount);
  }, [tablePageCount]);

  useEffect(() => {
    if (!isFetching && pagesHasChanged) {
      fetchNextPage?.({ pageIndex, pageSize });
    }
  }, [isFetching, pagesHasChanged, pageIndex, pageSize, fetchNextPage]);

  if (pageCount <= 1) return null;

  // If user want to control manually pageIndex (useLocalDataTableData hook),
  // Then manualPageIndex set directly to table will be used (DataTable pageIndex property)
  const pageIndexToSet =
    typeof manualPageIndex === 'number' ? manualPageIndex : Math.ceil((queryVariables?.page?.offset ?? 0) / limit);

  return (
    <StyledPaginator {...rest} data-test="pagination-wrapper">
      <Pagination
        count={tablePageCount || pageCount || 0}
        shape="rounded"
        page={pageIndexToSet + 1}
        onChange={(_, page) => {
          setPageCount(pageCount ?? 0);
          applyPage?.(page - 1);
          onPageChange?.(page - 1);
        }}
        disabled={isFetching}
        siblingCount={visibleExtraPages ?? 2}
        renderItem={(item) => {
          const isPreviousOrNext = item.type === 'previous' || item.type === 'next';
          if (isPreviousOrNext) {
            return <PaginationItem data-test={item.type} {...item} />;
          }
          return (
            <PaginationItem
              data-test-active={item.selected}
              data-test="page-button"
              data-test-page-number={item.page}
              {...item}
            />
          );
        }}
      />
    </StyledPaginator>
  );
};

const StyledPaginator = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  padding: 1rem 0;
  height: ${DATA_TABLE_PAGINATOR_HEIGHT_PX};
`;

export function getDataTablePageCount(totalCount: number, limit: number = 30) {
  if (limit === 0 || Number.isNaN(limit)) {
    return 1;
  }
  return Math.ceil(totalCount / limit);
}
